import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import api, { fmtMoney, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Plus, Briefcase, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/rbac";
import ConfirmSoftDelete from "../components/ConfirmSoftDelete";

const empty = { code: "", name: "", customer: "", manager: "", budget: 0, currency: "TRY", status: "active" };

export default function Projects() {
    const { user } = useAuth();
    const canWrite = can(user, "masterdata.write");
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const r = await api.get("/projects"); setItems(r.data); }
        finally { setLoading(false); }
    };
    useEffect(()=>{ load(); }, []);

    const submit = async () => {
        if (!form.code.trim() || !form.name.trim()) { toast.error("Kod ve ad zorunludur"); return; }
        setBusy(true);
        try {
            await api.post("/projects", { ...form, budget: Number(form.budget) });
            toast.success("Proje oluşturuldu");
            setOpen(false); setForm(empty); load();
        } catch(e){ toast.error(formatApiError(e)); }
        finally{ setBusy(false); }
    };

    const confirmDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/projects/${toDelete.id}`);
            toast.success("Proje geri dönüşüm kutusuna taşındı");
            setToDelete(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeleting(false); }
    };

    return (
        <AppShell
            title="Projeler"
            subtitle="Ana Veri"
            actions={
                canWrite ? (
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-project-btn" className="gap-2"><Plus size={16}/> Yeni Proje</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Yeni Proje</DialogTitle></DialogHeader>
                        <div className="grid grid-cols-2 gap-4">
                            <F label="Kod *"><Input data-testid="prj-code" value={form.code} onChange={(e)=>setForm({...form,code:e.target.value})}/></F>
                            <F label="Adı *"><Input data-testid="prj-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></F>
                            <F label="Müşteri"><Input value={form.customer} onChange={(e)=>setForm({...form,customer:e.target.value})}/></F>
                            <F label="Proje Yöneticisi"><Input value={form.manager} onChange={(e)=>setForm({...form,manager:e.target.value})}/></F>
                            <F label="Bütçe"><Input type="number" value={form.budget} onChange={(e)=>setForm({...form,budget:e.target.value})}/></F>
                            <F label="Para Birimi">
                                <select className="h-10 border border-input rounded-md px-3 bg-background text-sm" value={form.currency} onChange={(e)=>setForm({...form, currency:e.target.value})}>
                                    <option>TRY</option><option>USD</option><option>EUR</option>
                                </select>
                            </F>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={()=>setOpen(false)}>İptal</Button>
                            <Button data-testid="save-project" onClick={submit} disabled={busy}>
                                {busy && <Loader2 size={14} className="mr-2 animate-spin"/>}
                                Kaydet
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                ) : null
            }
        >
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading ? (
                    <div className="col-span-full text-center py-16 text-muted-foreground">
                        <Loader2 size={22} className="mx-auto mb-2 animate-spin opacity-70"/>
                        Yükleniyor...
                    </div>
                ) : items.map((p) => {
                    const pct = p.budget > 0 ? Math.min(100, (p.committed_cost / p.budget) * 100) : 0;
                    const over = pct >= 90;
                    return (
                        <div key={p.id} data-testid={`prj-card-${p.code}`} className="bg-card border border-border rounded-md p-5 hover:border-primary/30 transition-colors">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{p.code}</div>
                                    <div className="font-semibold text-[15px] mt-1">{p.name}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        {p.customer} • {p.manager || "—"}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                                        <Briefcase size={16}/>
                                    </div>
                                    {canWrite && (
                                        <button
                                            data-testid={`prj-delete-${p.code}`}
                                            onClick={() => setToDelete(p)}
                                            className="p-1 rounded hover:bg-rose-50 dark:hover:bg-rose-950/20 text-muted-foreground hover:text-rose-600"
                                            title="Geri dönüşüm kutusuna taşı"
                                        >
                                            <Trash2 size={13}/>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-2 mt-4">
                                <div className="flex justify-between text-xs">
                                    <span className="text-muted-foreground">Bütçe</span>
                                    <span className="font-medium tabular-nums">{fmtMoney(p.budget, p.currency)}</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                    <span className="text-muted-foreground">Taahhüt Edilen</span>
                                    <span className="font-medium tabular-nums">{fmtMoney(p.committed_cost, p.currency)}</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                    <span className="text-muted-foreground">Gerçekleşen</span>
                                    <span className="font-medium tabular-nums">{fmtMoney(p.actual_cost, p.currency)}</span>
                                </div>
                                <div className="h-1.5 bg-secondary rounded-full overflow-hidden mt-3">
                                    <div className={`h-full ${over ? "bg-rose-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                                </div>
                                <div className="flex justify-between text-[11px] pt-1">
                                    <span className={over ? "text-rose-600 font-medium" : "text-muted-foreground"}>%{pct.toFixed(1)} kullanıldı</span>
                                    <span className="text-muted-foreground">Kalan: {fmtMoney(Math.max(0, p.budget - p.committed_cost), p.currency)}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {!loading && items.length===0 && (
                    <div className="col-span-full text-center py-16 text-muted-foreground">
                        <Briefcase size={28} className="mx-auto mb-2 opacity-40"/>
                        Henüz proje yok
                    </div>
                )}
            </div>
            <ConfirmSoftDelete
                open={!!toDelete}
                onOpenChange={(o)=>!o && setToDelete(null)}
                onConfirm={confirmDelete}
                busy={deleting}
                itemLabel={toDelete ? `${toDelete.code} — ${toDelete.name}` : null}
                testid="confirm-delete-project"
            />
        </AppShell>
    );
}
function F({label,children}){return<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>}
